import { create } from 'zustand';
import { usePanel, type PanelId, type TabId } from './store.js';

// Cross-panel link for the SELECTED ECONOMIC GROUP, scoped to the platform's link
// CHANNELS: the Ownership Network panel broadcasts the group the user focused; the
// Group Overview panel reads from its own channel and renders the standalone detail
// (the Record-panel pattern, but for groups). Put both on the same channel (the
// coloured dot in each header) to link them — exactly like the entity channel, but
// carrying a group payload. Dedicated store (NOT the persisted app store) so member
// lists never bloat localStorage.

import type { OwnershipGroupNode } from '../api/client.js';

export interface ActiveGroup {
  /** The resolved group node as served by /ownership-network (members capped). */
  node: OwnershipGroupNode;
  /** The countries the broadcasting panel was filtered to (for labels). */
  countries: string[];
}

interface GroupLinkState {
  /** keyed by `${tabId}:${groupId}` (the channel). */
  byKey: Record<string, ActiveGroup | null>;
  setFor: (key: string, g: ActiveGroup | null) => void;
}

export const useGroupLink = create<GroupLinkState>((set) => ({
  byKey: {},
  setFor: (key, g) => set((s) => ({ byKey: { ...s.byKey, [key]: g } })),
}));

/** The group channel key for a panel — its current link group (channel), whether
 *  linked or pinned (uses the last group when pinned). */
export function useGroupKey(tabId: TabId, panelId: PanelId): string {
  const panel = usePanel(tabId, panelId);
  const gid = panel
    ? panel.link.kind === 'linked'
      ? panel.link.groupId
      : panel.link.lastGroupId
    : 'default';
  return `${tabId}:${gid}`;
}
